/**
 * Report data + helpers, ported from the live CIE report builder
 * (canadianinsulationexperts.com/report-builder.html → buildReport).
 *
 * A report uses the builder's own state shape — { brand, header, sections, quotes } —
 * so a report built in this app can later be published to the same /r/<token> viewer.
 */
import {
  AIR_SEALING_TEXT, DEPTH_BANDS, DEPTH_TEXT, EXHAUST_TEXT, HATCH_TEXT, MOULD_TEXT, PEST_TEXT, SOFFIT_TEXT,
} from './reportWording';
import { descriptionFor } from '../../services/quoteDoc';

// Brand block — mirrors the builder's defaultState().brand (same claims the live site makes)
export const BRAND = {
  company: 'Canadian Insulation Experts',
  brandName: 'Canadian Insulation Experts',
  tagline: 'INSULATION & HVAC · GTA',
  reportType: 'Attic Insulation Report',
  preparer: '', // the agent who did the inspection — filled in per report
  initials: 'CIE',
  accent: '#F47C20',
  phone: '(416) 990-4555',
  email: 'support@canadianinsulationexperts.com',
  website: 'canadianinsulationexperts.com',
  live: true,
  s1b: '10-Year', s1l: 'Workmanship Warranty',
  s2b: 'Up to $1,250', s2l: 'Government Rebates',
  s3b: 'WSIB', s3l: 'Certified & Fully Insured',
};

/* ---------- insulation materials (R-value per inch) + R-value colour ---------- */
const MATERIALS = [
  { name: 'Blown-in Fiberglass', rpi: 2.5 },
  { name: 'Blown-in Cellulose', rpi: 3.5 },
  { name: 'Fiberglass Batt', rpi: 3.2 },
  { name: 'Mineral Wool', rpi: 3.3 },
  { name: 'Open-cell Spray Foam', rpi: 3.6 },
  { name: 'Closed-cell Spray Foam', rpi: 6.5 },
  { name: 'None / bare', rpi: 0 },
];

const matRPI = (name) => {
  const m = MATERIALS.find((x) => x.name === name);
  return m ? m.rpi : 2.5;
};

// a report built from the attic inspection carries its own R-value (curR), worked out the same
// way the inspection page shows it to the agent — otherwise fall back to the builder's material table
export const metricCurR = (m) =>
  m && m.curR != null ? Math.round(m.curR) : Math.round((parseFloat(m && m.depth) || 0) * matRPI(m && m.material));

/* ---------- attic inspection R-value (shared with the inspection page's CALCULATIONS box) ---------- */
const ATTIC_R_PER_INCH = { cellulose: 3.2, fiberglass: 2.7, 'fiberglass batts': 3.5, other: 2.4 };
const ATTIC_DEPTH_FOR_R60 = { cellulose: '19', fiberglass: '22', 'fiberglass batts': '20', other: '25.0' };

// mixed materials are rated on the weakest one, so the estimate never flatters the attic
export const atticRValue = (area) => {
  const depth = parseFloat(area && area.existingDepth) || 0;
  const materials = (area && area.insulationMaterials) || [];
  let rpi = 3.0;
  let weakest = 'other';
  if (materials.length) {
    rpi = 999;
    materials.forEach((mat) => {
      const r = ATTIC_R_PER_INCH[mat] || 3.0;
      if (r < rpi) { rpi = r; weakest = mat; }
    });
  }
  const estR = depth * rpi;
  return { depth, estR, gapToR60: 60 - estR, depthNeeded: ATTIC_DEPTH_FOR_R60[weakest] || '20.0' };
};

// stored values keep the US spelling; the customer reads Canadian
const canadian = (text) => text.replace(/fiberglass/gi, (w) => (w[0] === 'F' ? 'Fibreglass' : 'fibreglass'));

const materialLabel = (materials) =>
  (materials || []).map((m) => canadian(m).replace(/\b\w/g, (c) => c.toUpperCase())).join(' + ');

/* status colour for an R-value: red -> amber -> green. Deliberately NOT the brand accent. */
export const rColor = (cur, tgt) => {
  const r = Number(tgt) > 0 ? cur / tgt : 0;
  return r < 0.6 ? '#c0392b' : r < 0.85 ? '#e08a1e' : '#4f8a33';
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const fmtDate = (iso) => {
  if (!iso) return '';
  const p = iso.split('-');
  if (p.length !== 3) return iso;
  return `${MONTHS[+p[1] - 1]} ${+p[2]}, ${p[0]}`;
};

// local calendar date — not toISOString(), which is UTC and rolls to tomorrow after 8pm Eastern
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// whole dollars print like the builder ("$2,906"); prices with cents (after HST) keep two decimals
export const money = (n) => {
  const v = Number(n);
  if (isNaN(v)) return '';
  return '$' + v.toLocaleString('en-CA', Number.isInteger(v) ? undefined : { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const lineCost = (l, sqft) => (l && l.area ? (Number(sqft) || 0) * (Number(l.rate) || 0) : Number(l && l.cost) || 0);

export const quoteTotal = (q) => {
  const sum = (q.lines || []).filter((l) => l.on).reduce((a, l) => a + lineCost(l, q.sqft), 0);
  const ov = q.price;
  if (ov !== '' && ov != null && String(ov).trim() !== '' && !isNaN(Number(ov))) return Number(ov);
  return sum;
};

/* ---------- quote packages from the app's quote builder ---------- */
const HST_RATE = 0.13;

// same maths as the quote pricing page: discount comes off the subtotal, HST goes on the discounted amount
export const packageTotal = (pkg) => {
  const subtotal = (pkg.services || []).reduce((sum, s) => sum + (s.quantity || 1) * (s.unitRate || 0), 0);
  let discount = 0;
  if (pkg.discount && pkg.discount.type === 'amount') discount = pkg.discount.value || 0;
  if (pkg.discount && pkg.discount.type === 'percent') discount = (subtotal * (pkg.discount.value || 0)) / 100;
  return Math.round((subtotal - discount) * (1 + HST_RATE) * 100) / 100;
};

/* ---------- demo content ---------- */
// Template findings — the standard sections with no customer data in them:
// no photos, no measured depth (so no current R-value), no job-specific numbers.
const TEMPLATE_SECTIONS = [
  {
    id: 1, title: 'Insulation Depth & Coverage', tag: 'Attic Insulation', accent: '#0D2D56', photos: [],
    bullets: [
      'We found blown-in fibreglass across your attic floor, well below the Ontario code target of 22 inches.',
      'Inadequate insulation in your attic is letting heated air escape in winter and cool air leak out in summer, forcing your heating and cooling systems to work harder.',
    ],
    metrics: { on: true, depth: '', material: 'Blown-in Fibreglass', tgtR: 60 },
  },
  {
    id: 2, title: 'Soffit Condition & Baffle Vents', tag: 'Attic Insulation', accent: '#0D2D56', photos: [],
    bullets: [
      'We found that blown-in or batted insulation has been installed up against some of the roof deck at the eaves, blocking the soffit vents.',
      'Blocked soffit vents trap warm, moist air in the attic, which can cause frost buildup on the roof sheathing in winter and accelerate shingle wear.',
    ],
    metrics: { on: false },
  },
  {
    id: 3, title: 'Air Sealing', tag: 'Attic Insulation', accent: '#0D2D56', photos: [],
    bullets: [
      'We found multiple unsealed penetrations through your attic floor including wiring chases, plumbing stacks, and the attic hatch perimeter.',
      'Unsealed penetrations let your heated air escape directly into the attic space all winter, working against your heating system.',
    ],
    metrics: { on: false },
  },
  {
    id: 4, title: 'Attic Hatch / Access', tag: 'Attic Insulation', accent: '#0D2D56', photos: [],
    bullets: [
      'The hatch cover lacks insulation and has no continuous seal around its perimeter.',
      'Heat escapes directly through the uninsulated hatch year-round, raising your heating costs and making rooms below feel colder.',
    ],
    metrics: { on: false },
  },
  {
    id: 5, title: 'Mould & Moisture Assessment', tag: 'Attic Insulation', accent: '#0D2D56', photos: [],
    bullets: [
      'We found active mould colonization across the roof sheathing and framing in the affected area.',
      'Mould growth this extensive can affect indoor air quality and may contribute to respiratory or allergy symptoms in your home.',
    ],
    metrics: { on: false },
  },
  {
    id: 6, title: 'Exhaust Systems & Venting', tag: 'Bathroom Exhaust', accent: '#f47c20', photos: [],
    bullets: [
      'Both bathroom exhausts have dedicated roof vents; however, the connections at the roof are not properly sealed, allowing moisture to leak back into the attic.',
      'This excess moisture can lead to condensation and mould growth, especially when the attic’s overall ventilation is insufficient.',
    ],
    metrics: { on: false },
  },
];

// Token for the generated report on the Previous reports tab
export const DEMO_REPORT_TOKEN = 'b41f7c2e9a0d4c6e8f3a5b1d7e9c2f60';

const DEMO_REPORTS = {
  [DEMO_REPORT_TOKEN]: {
    brand: BRAND,
    // blank template: every customer field stays empty until the inspection fills it in
    header: { customer: '', date: '', address: '', cityline: '', hero: '' },
    sections: TEMPLATE_SECTIONS,
    // no package is built in — "Choose your fix" lists exactly the packages quoted for the customer
    quotes: [],
    template: true,
  },
};

/* ---------- reports sent from this app ----------
   Kept for this session (the /r/<token> viewer reads them). The shared, permanent copy is
   services/drafts.js saveSent → Supabase inspection_records, which Previous reports lists. */
const savedReports = [];

const randomToken = () =>
  Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');

// meta: { leadId, source } — source is the inspection the report was built from, so Edit can reopen it
export const saveReport = (report, meta = {}) => {
  const token = randomToken();
  savedReports.unshift({ token, report, leadId: meta.leadId || null, source: meta.source || null, sentAt: new Date().toISOString() });
  return token;
};

export const getSavedReports = () => savedReports;

export const getReport = (token) => {
  if (DEMO_REPORTS[token]) return DEMO_REPORTS[token];
  const saved = savedReports.find((r) => r.token === token);
  return saved ? saved.report : null;
};

// Anything under /r is a customer link and must never fall through to the dashboard.
// Token rules match the live viewer: ?t=<token>, else the first alphanumeric run after /r/
// ("" → "Report not found"). Anything else → null (the dashboard).
export const reportTokenFromPath = (path, search = '') => {
  if (!/^\/r(\/|$)/.test(path || '')) return null;
  const q = new URLSearchParams(search).get('t');
  if (q) return q.trim();
  const m = /\/r\/([A-Za-z0-9]+)/.exec(path);
  return m ? m[1] : '';
};

/* Build a report from the inspection + quote flow (QuoteReview → send). */
// formats: { [packageId]: 'summary' | 'detailed' } — detailed shows each line's price to the customer
export const reportFromInspection = (inspection, packageData, formats = {}) => {
  const insp = inspection || {};
  const full = insp.serviceAddress || insp.address || '';
  const cut = full.indexOf(',');
  return {
    brand: BRAND,
    header: {
      customer: insp.customerName || insp.contactName || '',
      date: todayISO(),
      address: cut > -1 ? full.slice(0, cut).trim() : full,
      cityline: cut > -1 ? full.slice(cut + 1).trim() : '',
      hero: insp.heroPhoto || '',
    },
    // pre-fills "Book your job now" — the customer confirms or corrects these
    contact: { phone: insp.servicePhone || insp.phone || '', email: insp.email || '' },
    leadId: insp.id || null,
    // no attic inspection behind this report (quote-only) — show the empty template sections
    sections: insp.findings || TEMPLATE_SECTIONS,
    quotes: Object.values(packageData || {}).map((pkg, i) => ({
      id: pkg.id,
      name: pkg.name,
      subtitle: '',
      price: packageTotal(pkg),
      selected: i === 0,
      lines: (pkg.services || []).map((s, li) => ({
        id: li,
        label: s.name,
        desc: descriptionFor(s), // same text as the quote PDF
        category: s.category || '', // the service summary groups by it, like the PDF
        on: true,
        ...(formats[pkg.id] === 'detailed' ? { amount: (s.quantity || 1) * (s.unitRate || 0) } : {}),
      })),
    })),
  };
};

/* ---------- findings from the attic inspection ---------- */
// "cellulose", "fibreglass batts", "cellulose and fibreglass" — "insulation" when nothing named
const materialPhrase = (materials) => {
  const named = (materials || []).filter((m) => m !== 'other').map(canadian);
  return named.length ? named.join(' and ') : 'insulation';
};

const finding = (id, title, photos, bullets, extra = {}) => ({
  id, title, tag: 'Attic Insulation', accent: '#0D2D56', photos, bullets, metrics: { on: false }, ...extra,
});

const picked = (list, text) => (list || []).flatMap((option) => text[option] || []);
const num = (n) => Math.round(n).toLocaleString('en-CA');

const depthBand = (R) => DEPTH_BANDS.find((b) => R < b.max).id;

const mouldBand = (level, coverage) => {
  const pct = parseInt(coverage, 10) || 0;
  if (level === 'L1') return pct > 25 ? 'spreading' : 'early';
  if (level === 'L2') return pct >= 75 ? 'severe' : 'serious';
  return 'unrated';
};

/**
 * The report's findings, written from what the inspector entered — every sentence comes from
 * the approved wording in reportWording.js, with this customer's numbers filled in.
 * A section only appears when something was found there — nothing found or left blank, and it's
 * left out of the report entirely. Inspector notes stay internal.
 */
export const findingsFromAttic = (attic, { firstName = '' } = {}) => {
  const form = (attic && attic.formData) || {};
  const areas = (attic && attic.atticAreas) || [];
  const photos = (attic && attic.photos) || {};
  const slot = (key) => photos[key] || [];

  // 1.1 insulation depth — every area's ruler + overview photos; the R-value card uses area 1,
  // exactly like the CALCULATIONS box on the inspection page
  const depthPhotos = areas.flatMap((a) => [...slot(`depth-${a.id}`), ...slot(`overview-${a.id}`)]);
  const first = areas[0] || {};
  const r = atticRValue(first);
  const entered = (a) => a && a.existingDepth !== '' && a.existingDepth != null && !Number.isNaN(parseFloat(a.existingDepth));
  let depthBullets = [];
  if (entered(first)) {
    const R = Math.round(r.estR);
    const needed = parseFloat(r.depthNeeded) || 20;
    depthBullets = DEPTH_TEXT[depthBand(R)]({
      first: firstName,
      depth: r.depth,
      material: materialPhrase(first.insulationMaterials),
      R,
      needed: num(needed),
      more: num(Math.max(1, needed - r.depth)),
      sqft: parseFloat(first.atticSize) ? num(parseFloat(first.atticSize)) : '',
    });
    areas.slice(1).forEach((a, i) => {
      if (!entered(a)) return;
      const ar = atticRValue(a);
      depthBullets.push(DEPTH_TEXT.extraArea({ n: i + 2, depth: ar.depth, material: materialPhrase(a.insulationMaterials), R: Math.round(ar.estR) }));
    });
  }
  const sections = [];
  const add = (f) => f.bullets.length && sections.push(f);

  add(finding(1, 'Insulation Depth & Coverage', depthPhotos, depthBullets, {
    metrics: {
      on: true,
      depth: r.depth || '',
      material: materialLabel(first.insulationMaterials),
      tgtR: 60,
      curR: entered(first) ? r.estR : null,
    },
  }));

  // 1.2 soffits & baffles · 1.3 air sealing — one approved paragraph per option ticked
  add(finding(2, 'Soffit Condition & Baffle Vents', slot('soffit'), picked(form.soffitBaffleState, SOFFIT_TEXT)));
  add(finding(3, 'Air Sealing', slot('airSealing'), picked(form.airSealingIssues, AIR_SEALING_TEXT)));

  // 1.4 attic hatch — no hatch questions yet, so it appears once the hatch is photographed
  if (slot('hatch').length) add(finding(4, 'Attic Hatch / Access', slot('hatch'), HATCH_TEXT));

  // 1.5 bath & washroom exhaust — into the attic or to the exterior; not found / unconfirmed → left out
  add(finding(5, 'Exhaust Systems & Venting', slot('exhaust'), EXHAUST_TEXT[form.bathFanTermination] || [], {
    tag: 'Bathroom Exhaust',
    accent: '#f47c20',
  }));

  // 1.6 mould — banded by level × coverage; only when found
  if (form.moldDetected === 'yes') {
    const band = mouldBand(form.moldSeverityLevel, form.moldCoverageArea);
    add(finding(6, 'Mould & Moisture Assessment', slot('mold'), MOULD_TEXT[band]({ coverage: form.moldCoverageArea })));
  }

  // 1.7 pest evidence — only when found
  if (form.pestEvidence === 'yes') add(finding(7, 'Pest & Animal Activity', slot('pest'), PEST_TEXT));
  return sections;
};

/* The full report from the inspection flow: details step + front photo + attic inspection + quotes. */
export const buildInspectionReport = ({ lead, details, frontPhoto, attic, packages, formats, preparer }) => {
  const base = reportFromInspection(
    {
      id: lead && lead.id,
      customerName: details.fullName,
      serviceAddress: details.streetAddress,
      servicePhone: details.phone,
      email: details.email,
      heroPhoto: frontPhoto,
    },
    packages,
    formats
  );
  // the lead's agent, else whoever is using the app
  const agent = ((lead && lead.agentName) || preparer || '').split(' ')[0];
  return { ...base, brand: { ...BRAND, preparer: agent }, sections: findingsFromAttic(attic, { firstName: ((details && details.fullName) || '').trim().split(/\s+/)[0] }) };
};
